import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Container,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { 
  RestaurantMenu as CutleryIcon,
  LocalBar as BarIcon,
  Star as StarIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from "@mui/icons-material";

const Header = ({ restaurantInfo }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const mobileMenuContent = (
    <Box
      sx={{
        width: 280,
        height: "100%",
        background: "linear-gradient(135deg, rgba(20, 20, 20, 0.98) 0%, rgba(30, 30, 30, 0.95) 100%)",
        color: "white",
        padding: 2,
      }}
    >
      {/* Menu Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Contact Info
        </Typography>
        <IconButton 
          onClick={toggleMobileMenu}
          sx={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)", mb: 2 }} />

      {/* Phone Information */}
      {restaurantInfo.phone && (
        <List>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <PhoneIcon sx={{ color: "#4caf50" }} />
            </ListItemIcon>
            <Box>
              <Typography variant="caption" sx={{ color: "#a0aec0", display: "block" }}>
                Call Us
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "600", color: "white" }}>
                {restaurantInfo.phone}
              </Typography>
            </Box>
          </ListItem>
        </List>
      )}

      {/* Address Information */}
      {restaurantInfo.address && (
        <List>
          <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
            <ListItemIcon sx={{ minWidth: 40, pt: 0.5 }}>
              <LocationIcon sx={{ color: "#4caf50" }} />
            </ListItemIcon>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: "#a0aec0", display: "block" }}>
                Visit Us
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: "600", 
                  color: "white",
                  lineHeight: 1.3 
                }}
              >
                {restaurantInfo.address}
              </Typography>
            </Box>
          </ListItem>
        </List>
      )}

      {/* Additional Info */}
      <Box sx={{ mt: 3, p: 2, background: "rgba(255, 255, 255, 0.05)", borderRadius: 2 }}>
        <Typography variant="body2" sx={{ color: "#a0aec0", textAlign: "center" }}>
          We look forward to serving you!
        </Typography>
      </Box>
    </Box>
  );

  return (
    <AppBar 
      position="static" 
      sx={{ 
        background: `
          linear-gradient(
            135deg,
            rgba(20, 20, 20, 0.98) 0%,
            rgba(30, 30, 30, 0.95) 100%
          )
        `,
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        color: "white",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ py: 1, minHeight: { xs: '70px !important', md: '80px !important' } }}>
          {/* Logo Section */}
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: 50, md: 60 },
                height: { xs: 50, md: 60 },
                borderRadius: "16px",
                background: `
                  linear-gradient(
                    135deg,
                    rgba(76, 175, 80, 0.2) 0%,
                    rgba(56, 142, 60, 0.3) 100%
                )`,
                border: "1px solid rgba(76, 175, 80, 0.3)",
                mr: { xs: 2, md: 3 },
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `
                    radial-gradient(
                      circle at 30% 30%,
                      rgba(76, 175, 80, 0.4) 0%,
                      transparent 70%
                    )
                  `,
                },
              }}
            >
              <CutleryIcon 
                sx={{ 
                  fontSize: { xs: 28, md: 32 },
                  color: "#4caf50",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                  zIndex: 2,
                  position: "relative",
                }} 
              />
            </Box>
            
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #fff 0%, #ccc 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  fontSize: { 
                    xs: '1.5rem',   // Mobile: 24px
                    sm: '1.75rem',  // Small tablet: 28px  
                    md: '2rem',     // Tablet: 32px
                    lg: '2.125rem'  // Desktop: 34px (reduced from default h4)
                  },
                  lineHeight: 1.2,
                }}
              >
                {restaurantInfo.name || "The Velvet Terrace Abuja"}
              </Typography>
              
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={<StarIcon sx={{ fontSize: 16, color: "#f7f5efff" }} />}
                  label="Premium Lounge"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 215, 0, 0.1)",
                    color: "#f5f4efff",
                    fontWeight: "600",
                    border: "1px solid rgba(255, 215, 0, 0.3)",
                    backdropFilter: "blur(10px)",
                    height: 24,
                    fontSize: "0.75rem",
                  }}
                />
                <Chip
                  icon={<BarIcon sx={{ fontSize: 16, color: "#4caf50" }} />}
                  label="Signature Cocktails"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    color: "#4caf50",
                    fontWeight: "600",
                    border: "1px solid rgba(76, 175, 80, 0.3)",
                    backdropFilter: "blur(10px)",
                    height: 24,
                    fontSize: "0.75rem",
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Contact Info - Desktop */}
          <Box sx={{ 
            display: { xs: 'none', lg: 'flex' }, 
            alignItems: "center", 
            gap: { lg: 2, xl: 3 },
            flexShrink: 0
          }}>
            {restaurantInfo.phone && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <PhoneIcon 
                    sx={{ 
                      fontSize: 20,
                      color: "#4caf50",
                    }} 
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#a0aec0", display: "block" }}>
                    Call Us
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: "600", 
                      color: "white",
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {restaurantInfo.phone}
                  </Typography>
                </Box>
              </Box>
            )}

            {restaurantInfo.address && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <LocationIcon 
                    sx={{ 
                      fontSize: 20,
                      color: "#4caf50",
                    }} 
                  />
                </Box>
                <Box sx={{ maxWidth: 200, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "#a0aec0", display: "block" }}>
                    Visit Us
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: "600", 
                      color: "white",
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {restaurantInfo.address}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Mobile Menu Button with Animation */}
          <IconButton 
            onClick={toggleMobileMenu}
            sx={{ 
              color: "white",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
              transition: "all 0.3s ease",
              transform: mobileMenuOpen ? "rotate(90deg)" : "rotate(0deg)",
              display: { xs: 'flex', lg: 'none' }
            }}
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Toolbar>
      </Container>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            background: 'transparent',
            border: 'none',
          },
        }}
      >
        {mobileMenuContent}
      </Drawer>

      {/* Decorative Bottom Border */}
      <Box
        sx={{
          height: "2px",
          background: "linear-gradient(90deg, transparent, #4caf50, transparent)",
          margin: "0 20%",
          borderRadius: "2px",
        }}
      />
    </AppBar>
  );
};

export default Header;